import React, {
    lazy,
    Suspense,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import PropTypes from 'prop-types';

import { useIntl } from 'react-intl';
import { Button } from '@sumologic/yama-ds-components';

import customFieldsNumbers, {
    customFieldsLiterals
} from '~/modules/customFieldTypes';

import { haveMarkup, markup2text } from '~/modules/helpers';
import { toErrorMessage } from '~/modules/helpers/error';

import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';
import NoDataToShow from '~/containers/NoDataToShow/NoDataToShow';
import NoResult from '~/containers/NoResult/NoResult';
import PreferencesContext from '~/containers/Preferences/PreferencesContext';

import Loader from '~/components/Loader/Loader';
import Translate from '~/components/Translate/Translate';

const Grid = lazy(() =>
    import(/* webpackChunkName: "ag-grid" */ './Elements/Grid/Grid')
);

import { noDataColFactory, toHeaderName } from './Framework/Columns/defs';
import { columnTypes, noDataColType } from './Framework/Columns/types';
import {
    iconsInRowColId,
    shouldConsiderRowEventCallback
} from './Framework/Rows/clicks';

import { createIconsInRowGroup } from './Framework/CellRender/iconsInRow';

import { involvedIncidentsCellRender } from './Framework/CellRender/involvedIncidents';
import { iconStatusCellRender } from './Framework/CellRender/iconStatus';
import { html2textCellRender } from './Framework/CellRender/html2text';
import { checkboxCellRender } from './Framework/CellRender/checkbox';
import { colorCellRender } from './Framework/CellRender/color';
import { tagsCellRender } from './Framework/CellRender/tags';
import { textCellRender } from './Framework/CellRender/text';
import { triggerLinkCellRender } from './Framework/CellRender/triggerLink';
import { durationCellRender } from './Framework/CellRender/duration';

//----------------------------------------------------------------------------------------------------------------------

// 🚩 𝘥𝘰 𝘯𝘰𝘵 𝘶𝘴𝘦 𝘵𝘩𝘦𝘴𝘦 𝘳𝘦𝘴𝘦𝘳𝘷𝘦𝘥 𝘤𝘭𝘢𝘴𝘴𝘦𝘴: 'active-row'
const defaultRowClassRules = {
    'no-data': row => !row.data,
    'is-new': row => row.data?.isNew,
    'is-child': row => row.data?.isChild,
    'is-father': row => row.data?.isFather,
    'is-private': row => row.data?.private,
    'is-overnode': row => row.data?.isOverNode,
    'is-disabled': row => !(row.data?.enabled ?? true),
    'is-deleting': row => row.data?.deleting,
    'is-deleted': row => {
        const e = row.data;
        return (
            e && (e.deleted || e.is_deleted || e.is_dismissed || !!e.isDeleted)
        );
    },
    'status-not-executed': row => 0 === row.data?.statusNumeric
};

const prefix_table2 = 'table2';
const suffix_firstDataRendered = 'first-data-rendered';

const path_centerColsClipper = '.ag-body-viewport > .ag-center-cols-clipper';
const path_centerColsViewport = `${path_centerColsClipper} > .ag-center-cols-viewport`;
const path_centerColsContainer = `${path_centerColsViewport} > .ag-center-cols-container`;

const selector_tableHasRow = `.${prefix_table2}.has-row`; // it mean "rows"
const selector_tableRowsContainer = `${selector_tableHasRow}.is-${suffix_firstDataRendered} ${path_centerColsContainer}`;

export const selector_firstRowForData = `${selector_tableRowsContainer} > .ag-row[row-index="0"]`;

const lbCurrentKey = 'lbCurrent';
const lbSelectedRowsCounterKey = 'lbSelectedRowsCounter';
const lbRecordCountInfiniteKey = 'lbRecordCountInfinite';

const inputGoToPageKey = 'goToPage';

// ag-grid.com/react-data-grid/row-models/#when-to-use
const rowModelTypeClientSide = 'clientSide';
const rowModelTypeInfinite = 'infinite';

//----------------------------------------------------------------------------------------------------------------------

const NoRowsPlaceholder = props => {
    const [show, setShow] = useState(false);
    const hasShownRef = useRef(false);

    useEffect(() => {
        if (!props.visible) {
            setShow(false);
            return;
        }

        if (hasShownRef.current) {
            setShow(true);
            return;
        }

        const timer = setTimeout(() => {
            setShow(true);
            hasShownRef.current = true;
        }, props.delay);

        return () => clearTimeout(timer);
    }, [props.visible, props.delay]);

    if (!show) return null;

    return props.searched ? (
        <>
            <NoResult
                className="no-results"
                description={
                    <Translate
                        message={
                            'no results match your search. Try to be less specific or check your spelling.'
                        }
                    />
                }
                actions={
                    props.clearFilter ? (
                        <div className="mt-4">
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={props.clearFilter}
                                data-sumo-test="reset-filter-test-button"
                                className="reset-filter-test-button"
                            >
                                <Translate message={'reset'} />
                            </Button>
                        </div>
                    ) : null
                }
            />
        </>
    ) : (
        <NoDataToShow
            className="no-rows"
            {...(props.createButtonShown && {
                description: (
                    <Translate message="create something awesome to see content here" />
                ),
                title: <Translate message={"you don't have any content yet!"} />
            })}
        />
    );
};

NoRowsPlaceholder.propTypes = {
    visible: PropTypes.bool,
    delay: PropTypes.number,
    searched: PropTypes.bool,
    clearFilter: PropTypes.func,
    createButtonShown: PropTypes.bool
};

//----------------------------------------------------------------------------------------------------------------------

/**
 * Table2 component for displaying data grids with filtering and pagination.
 *
 * @requires PreferencesContext - If not provided as an ancestor, the reset/clear filter button
 * will not appear for preference-driven filters. To ensure the reset button works without
 * PreferencesContext, provide the onClearFilter prop explicitly.
 */
// prettier-ignore
const Table2 = props => {
    const {
        id,
        header,
        showHeader = true,
        showSelectedRowsCounter = true,

        enableGoToPage,
        highlightRowOnClick,
        checkboxSelectorLeft,
        suppressRowClickToggle,

        externalRowSelection,
        handleSelectAllRows,

        getTableWrapperRef,

        getSetCheckedAllRowsAs,
        getTableDataSource,
        getUnselectedRows,
        getRefreshTable,
        getNoRow,

        iconsInRow,

        rowsPerPageMin,
        rowModelType,
        fetchDataOnce,
        fetchPage,

        filterString,
        currentDetail,
        rowToBeClicked, // 𝘵𝘳𝘪𝘨𝘨𝘦𝘳 𝘢 𝘱𝘳𝘰𝘨𝘳𝘢𝘮𝘮𝘢𝘵𝘪𝘤 𝘤𝘭𝘪𝘤𝘬, 𝘪𝘧 𝘤𝘭𝘪𝘤𝘬𝘦𝘥𝘙𝘰𝘸𝘖𝘣𝘫 𝘰𝘣𝘫𝘦𝘤𝘵 𝘩𝘢𝘴 𝘪𝘥 𝘴𝘦𝘢𝘳𝘤𝘩 𝘪𝘯 𝘳𝘰𝘸...

        rowKey = 'id',
        rows: propsRows,
        forceTooltip = false,

        ignoreDeletedRowClick,

        // ag-grid.com/javascript-grid-row-styles
        rowClassRules: propsRowClassRules,

        // ag-grid.com/react-data-grid/component-cell-renderer/
        cellRenders,

        // ag-grid.com/react-data-grid/row-height/#rowheight-property
        rowHeight = 35,

        // ag-grid.com/react-data-grid/grid-properties/#reference-selection
        suppressRowClickSelection,

        //ag-grid.com/react-data-grid/grid-properties/#reference-pagination
        pagination,

        //ag-grid.com/react-data-grid/grid-interface/#access-the-grid--column-api-1
        onGridReady,

        //ag-grid.com/react-data-grid/grid-events/#reference-selection
        onCellFocused,

        //ag-grid.com/react-data-grid/grid-events/#reference-miscellaneous
        onFirstDataRendered,
        onPaginationChanged,

        //ag-grid.com/react-data-grid/grid-events/#selection-events
        onSelectionChanged,
        onRowSelected,
        onRowClicked,

        //ag-grid.com/react-data-grid/grid-events/#reference-columns
        onColumnResized,

        // ag-grid.com/react-data-grid/column-definitions/#default-column-definitions
        defaultColDef,

        // ag-grid.com/react-data-grid/column-definitions/
        columnDefs,
        disabled = false,

        // Custom callback for clearing filters (used in no-results action)
        onClearFilter,

        createButtonShown,

        'data-testid': testid,
        ...rest
    } = props;

    // console.debug({ rest });

    // 𝘱𝘢𝘴𝘴𝘦𝘥 𝘥𝘰𝘸𝘯 𝘸𝘪𝘵𝘩𝘰𝘶𝘵 𝘮𝘢𝘯𝘪𝘱𝘶𝘭𝘢𝘵𝘪𝘰𝘯, 𝘣𝘶𝘵 𝘳𝘦𝘧𝘦𝘳𝘦𝘯𝘤𝘦𝘥 𝘢𝘵 𝘵𝘩𝘪𝘴 𝘭𝘦𝘷𝘦𝘭: (𝘧𝘪𝘹 𝘢𝘵𝘵𝘳𝘪𝘣𝘶𝘵𝘦 𝘱𝘳𝘦𝘴𝘦𝘯𝘤𝘦)
    const proxied = {
        rowHeight,
        pagination,
        suppressRowClickToggle,
        suppressRowClickSelection
    };

    /*
     NOTE:
         𝘢𝘮𝘰𝘯𝘨 𝘵𝘩𝘦 𝘱𝘳𝘰𝘱𝘦𝘳𝘵𝘪𝘦𝘴 𝘵𝘩𝘢𝘵 𝘤𝘢𝘯 𝘣𝘦 𝘱𝘢𝘴𝘴𝘦𝘥 𝘸𝘪𝘵𝘩 𝘳𝘦𝘴𝘵 𝘸𝘦 𝘩𝘢𝘷𝘦:

            domLayout='autoHeight' // "normal" 𝘪𝘴 𝘵𝘩𝘦 𝘥𝘦𝘧𝘢𝘶𝘭𝘵 𝘪𝘧 𝘯𝘰𝘵𝘩𝘪𝘯𝘨 𝘪𝘴 𝘴𝘱𝘦𝘤𝘪𝘧𝘪𝘦𝘥

         www.ag-grid.com/react-data-grid/grid-size/#dom-layout
         𝘋𝘰𝘯'𝘵 𝘶𝘴𝘦 𝘎𝘳𝘪𝘥 𝘈𝘶𝘵𝘰 𝘏𝘦𝘪𝘨𝘩𝘵 𝘸𝘩𝘦𝘯 𝘥𝘪𝘴𝘱𝘭𝘢𝘺𝘪𝘯𝘨 𝘭𝘢𝘳𝘨𝘦 𝘯𝘶𝘮𝘣𝘦𝘳𝘴 𝘰𝘧 𝘳𝘰𝘸𝘴

     DEPRECATED:
        tableOptions
    */

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const validatedRowModelType =
            fetchPage
                ? rowModelTypeInfinite
                : [
                    rowModelTypeClientSide,
                    rowModelTypeInfinite
                  ].includes(rowModelType)
                    ? rowModelType
                    : rowModelTypeClientSide;

    const rowsAreInfinite = rowModelTypeInfinite === validatedRowModelType;
    const isServerSide = rowsAreInfinite;

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const intl = useIntl();
    const { formatMessage } = intl;

    // 𝗇𝗈𝗍𝖾: 𝘪𝘵 𝘧𝘢𝘪𝘭 𝘴𝘪𝘭𝘦𝘯𝘵𝘭𝘺...
    const translate = (text, values) => {
        if (text) {
            const lowerCaseText = text.toLowerCase();
            return intl.messages[lowerCaseText]
                ? formatMessage(
                      {
                          defaultMessage: text,
                          id: lowerCaseText
                      },
                      values || null
                  )
                : text;
        }
        return '';
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const ref = useRef({
        tableMap: new Map()
    }).current;

    const resetRowSelections = () => {
        ref.selectedRows = [];
        ref.unselectedRows = [];
        ref.gridApi?.api?.deselectAll();
    };

    const setTableMapElemTextContent = (key, value) => {
        const el =  ref.tableMap.get(key);
        if (el) {
            el.textContent = value;
        }
    };

    const setlbSelectedRowsCounter = value => {
        setTableMapElemTextContent(
            lbSelectedRowsCounterKey,
            value
        );
    };

    const updateInfiniteLastRowMax = value => {
        //todo
        setLastRowMax(value);
        setTableMapElemTextContent(
            lbRecordCountInfiniteKey,
            value
        );
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const stickyAlertContext = useContext(StickyAlertContext);
    const preferencesContext = useContext(PreferencesContext);

    const notifyError = (title, exception) => {
        if (APP_INFO.debug) {
            console.debug(exception);
        }
        if (ref.isMounted) {
            const text = toErrorMessage(exception);
            stickyAlertContext.notifyError({
                title: title,
                message:
                    text?.length
                        ? haveMarkup(text) // todo: ASAP check if can remove that control
                            ? markup2text(text)
                            : text
                        : translate(
                            'An error occurred. {error}',
                            { error: exception.statusText }
                          )
            });
        }
    };

    const notifyFetchError = exception => {
        notifyError(
            translate('Fetch error'),
            exception
        );
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const [rowsDataOnce, setRowsDataOnce] = useState(null);

    const [lastRowMax, setLastRowMax] = useState(0);

    // const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageMin || 150);
    const rowsPerPage = rowsPerPageMin || 150;

    // const [cacheBlockSize, setCacheBlockSize] = useState(rowsPerPage * 3);
    // const [maxBlocksInCache, setMaxBlocksInCache] = useState(cacheBlockSize * 100);
    /* note:
       "cacheBlockSize" 𝘪𝘴 𝘩𝘰𝘸 𝘮𝘢𝘯𝘺 𝘳𝘰𝘸𝘴 𝘦𝘢𝘤𝘩 𝘣𝘭𝘰𝘤𝘬 𝘪𝘯 𝘵𝘩𝘦 𝘤𝘢𝘤𝘩𝘦 𝘴𝘩𝘰𝘶𝘭𝘥 𝘤𝘰𝘯𝘵𝘢𝘪𝘯: 𝘦𝘢𝘤𝘩 𝘤𝘢𝘭𝘭 𝘵𝘰 𝘺𝘰𝘶𝘳 𝘥𝘢𝘵𝘢𝘴𝘰𝘶𝘳𝘤𝘦 𝘸𝘪𝘭𝘭 𝘣𝘦 𝘧𝘰𝘳 𝘰𝘯𝘦 𝘣𝘭𝘰𝘤𝘬.
       𝘛𝘩𝘦𝘯 𝘢𝘴 𝘺𝘰𝘶 𝘴𝘤𝘳𝘰𝘭𝘭 𝘥𝘰𝘸𝘯, 𝘱𝘳𝘦𝘷𝘪𝘰𝘶𝘴 𝘣𝘭𝘰𝘤𝘬𝘴 𝘸𝘪𝘭𝘭 𝘣𝘦 𝘥𝘪𝘴𝘤𝘢𝘳𝘥𝘦𝘥 𝘢𝘯𝘥 𝘸𝘪𝘭𝘭 𝘣𝘦 𝘭𝘰𝘢𝘥𝘦𝘥 𝘢𝘨𝘢𝘪𝘯 𝘪𝘧 𝘵𝘩𝘦 𝘶𝘴𝘦𝘳 𝘴𝘤𝘳𝘰𝘭𝘭𝘴 𝘣𝘢𝘤𝘬 𝘶𝘱.
       𝘛𝘩𝘦 𝘨𝘳𝘪𝘥 𝘬𝘦𝘦𝘱𝘴 𝘵𝘩𝘦 𝘣𝘭𝘰𝘤𝘬𝘴 𝘪𝘯 𝘢 𝘤𝘢𝘤𝘩𝘦: 𝗒𝗈𝗎 𝗁𝖺𝗏𝖾 𝗍𝗁𝖾 𝖼𝗁𝗈𝗂𝖼𝖾 𝗍𝗈 𝗇𝖾𝗏𝖾𝗋 𝖾𝗑𝗉𝗂𝗋𝖾 𝗍𝗁𝖾 𝖻𝗅𝗈𝖼𝗄𝗌, 𝗈𝗋 𝗍𝗈 𝗌𝖾𝗍 𝖺 𝗅𝗂𝗆𝗂𝗍.
    */

    const [paginationLocked, setPaginationLocked] = useState(false);

    const [firstDataRendered, setFirstDataRendered] = useState(false);

    const [targetRow, setTargetRow] = useState(null);

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const [fetchingData, setFetchingData] = useState(false);
    const [fetchingDbCount, setFetchingDbCount] = useState(false);

    const flagFetchedAs = bool => {
        if (ref.isMounted) {
            ref.canSetDatasource = bool;
            const flag = !bool;
            /**-/
            console.groupCollapsed('next', { flag });
                console.log('prev', {
                    fetchingDbCount,
                    fetchingData
                });
                //console.trace(ref);
            console.groupEnd();
            /**/
            setFetchingDbCount(flag);
            setFetchingData(flag);
        }
    };

    const refreshTable = params => {
        clearTimeout(ref.refreshTableTimeout);
        ref.lastFetchPageParams = null;
        let modelRefreshFn;
        if (isServerSide) {
            modelRefreshFn = api => {
                if (api) {
                    const gridOptionsDatasource = api.gridOptionsWrapper?.gridOptions?.datasource;
                    if (gridOptionsDatasource) {
                        api.refreshInfiniteCache(); // 𝘔𝘢𝘳𝘬𝘴 𝘢𝘭𝘭 𝘵𝘩𝘦 𝘤𝘶𝘳𝘳𝘦𝘯𝘵𝘭𝘺 𝘭𝘰𝘢𝘥𝘦𝘥 𝘣𝘭𝘰𝘤𝘬𝘴 𝘪𝘯 𝘵𝘩𝘦 𝘤𝘢𝘤𝘩𝘦 𝘧𝘰𝘳 𝘳𝘦𝘭𝘰𝘢𝘥
                        // ag-grid.com/archive/26.0.0/react-data-grid/infinite-scrolling/#changing-the-datasource
                        api.setDatasource(gridOptionsDatasource);
                        params?.callback?.(api);
                    }
                } // else reschedule?
            };
        }
        ref.refreshTableTimeout = setTimeout(
            () => {
                ref.lastFetchPageParams = null;
                try {
                    modelRefreshFn?.(ref.gridApi?.api);
                } catch (exception) {
                    console.error(exception);
                }
            },
            params?.delay || 0
        );
    };

    getRefreshTable?.(refreshTable);

    const [visibleColumns] = useState({});
    visibleColumns.defs = useMemo(() => {
        const defsFiltered = columnDefs?.filter(e => !e.hide);
        if (!noRow) {
            let needRefreshTable = true;
            const defsPrev = visibleColumns.previous;
            if (defsPrev) {
                if (defsFiltered.length < defsPrev.length) {
                    const prevFields = defsPrev.map(e => e.field);
                    needRefreshTable = defsFiltered.some(e => !prevFields.includes(e.field));
                }
            }
            if (needRefreshTable) {
                refreshTable();
            }
        }
        return (visibleColumns.previous = defsFiltered);
    }, [
        columnDefs // 𝘪𝘯𝘵𝘦𝘳𝘯𝘢𝘭𝘭𝘺 𝘶𝘱𝘥𝘢𝘵𝘦𝘥 𝘣𝘺 𝘳𝘦𝘧𝘦𝘳𝘦𝘯𝘤𝘦
    ]);

    const datasource =
        (isServerSide && fetchPage)
            ? {
                rowCount: null, // 𝘣𝘦𝘩𝘢𝘷𝘦 𝘢𝘴 𝘪𝘯𝘧𝘪𝘯𝘪𝘵𝘦 𝘴𝘤𝘳𝘰𝘭𝘭
                pageSize: rowsPerPage,
                getRows: params => {
                    clearTimeout(ref.getRowsTimeout);

                    const {
                        startRow,
                        // endRow, // related to 'cacheBlockSize'
                        sortModel: eligibleSortModel,
                        successCallback,
                        failCallback
                    } = params;

                    const pageNum = 1 + (startRow > 0 ? startRow / rowsPerPage : 0);

                    const columns = visibleColumns.defs;

                    const newFetchPageParams = {
                        pageNum,
                        rowsPerPage,
                        eligibleSortModel,
                        filterString,
                        columns
                    };

                    // 𝘮𝘪𝘵𝘪𝘨𝘢𝘵𝘦 𝘴𝘢𝘮𝘦 𝘳𝘦𝘲𝘶𝘦𝘴𝘵𝘴 𝘳𝘦𝘱𝘦𝘵𝘪𝘵𝘪𝘰𝘯
                    const lastFetchPageParams = ref.lastFetchPageParams;

                    const shouldAcceptRequest = (
                        !lastFetchPageParams ||
                        Object.entries(lastFetchPageParams).some(([key,value]) => {
                            return (value || 0 === value) && value !== newFetchPageParams[key];
                        })
                    );

                    // console.log({ lastFetchPageParams, newFetchPageParams, shouldAcceptRequest, filterString });

                    if (shouldAcceptRequest) {
                        // Set fetchingData immediately to hide "No result" placeholder without delay
                        flagFetchedAs(false);

                        const requestStillValid = () => {
                            const actualFetchPageParams = ref.lastFetchPageParams;
                            // noinspection UnnecessaryLocalVariableJS
                            const bool = ref.isMounted ? newFetchPageParams === actualFetchPageParams: false;
                            // console.log({ requestStillValid: bool, newFetchPageParams, actualFetchPageParams });
                            return bool;
                        };
                        const handleNoRow = bool => {
                            setNoRow(bool);
                            getNoRow?.(bool);
                        };
                        ref.getRowsTimeout = setTimeout(() => {
                            const api = ref.gridApi?.api;
                            if (api) {
                                const gridDivEl = api.gridOptionsWrapper.environment.eGridDiv;
                                const agPagingPanel = gridDivEl?.querySelector('.ag-paging-panel');
                                const rowSummaryPanel = agPagingPanel?.querySelector('.ag-paging-row-summary-panel');
                                const pageSummaryPanel = agPagingPanel?.querySelector('.ag-paging-page-summary-panel');

                                const showEl = (el, bool) => {
                                    if (el) {
                                        el.classList[
                                            bool
                                                ? 'remove'
                                                : 'add'
                                        ]('d-none');
                                    }
                                };

                                const showSummaryPanel = bool => {
                                    showEl(rowSummaryPanel, bool);
                                    showEl(pageSummaryPanel, bool);
                                };

                                /*
                                // noinspection CommaExpressionJS
                                const textNodeLoadingRows =
                                    agPagingPanel
                                        ? (
                                            agPagingPanel.textNodeLoadingRows?.remove(),
                                            agPagingPanel.textNodeLoadingRows =
                                                agPagingPanel.appendChild(
                                                    agPagingPanel.ownerDocument.createTextNode(
                                                        translate('Loading rows...')
                                                    )
                                          ))
                                        : null;
                                */

                                // console.log({ agPagingPanel,textNodeLoadingRows });

                                const done = bool => {
                                    // bool && textNodeLoadingRows?.remove();
                                    showSummaryPanel(bool);
                                    flagFetchedAs(bool);
                                };


                                done(false);
                                ref.lastFetchPageParams = newFetchPageParams;
                                const fetchPromise = fetchPage(
                                    pageNum,
                                    rowsPerPage,
                                    eligibleSortModel,
                                    filterString,
                                    columns
                                );
                                if (fetchPromise?.then) {
                                    fetchPromise.then(result => {
                                        if (requestStillValid()) {
                                            const {
                                                data: {
                                                    rows,
                                                    count = null
                                                },
                                                getTotalRecordsCount // 𝘱𝘳𝘰𝘮𝘪𝘴𝘦 𝘢 𝘴𝘦𝘤𝘰𝘯𝘥 𝘳𝘦𝘲𝘶𝘦𝘴𝘵
                                            } = result;
                                            const noRowBool = rows ? rows.length < 1 : true;
                                            handleNoRow(noRowBool);
                                            if (count !== null) {
                                                const lastRow = pageNum * rowsPerPage + (1 + rowsPerPage);
                                                const nextLastRow = lastRow > count ? count : lastRow;
                                                updateInfiniteLastRowMax(count);
                                                getTableDataSource?.(successCallback, rows, nextLastRow);
                                                // console.info('fetchPage 2/2',{rows,nextLastRow});
                                                successCallback(rows, nextLastRow);
                                                return 1;
                                            } else {
                                                getTableDataSource?.(successCallback, rows);
                                                // console.info('fetchPage 1/2',{rows});
                                                successCallback(rows);
                                                if (noRowBool) {
                                                    return 2;
                                                } else {
                                                    if (getTotalRecordsCount) {
                                                        /*
                                                        let textNodeCountInProgress;
                                                        if (rowSummaryPanel) {
                                                            rowSummaryPanel.textNodeCountInProgress?.remove();
                                                            rowSummaryPanel.append(
                                                                textNodeCountInProgress =
                                                                    rowSummaryPanel.textNodeCountInProgress =
                                                                        rowSummaryPanel.ownerDocument.createTextNode(
                                                                            translate('Count in progress')
                                                                        )
                                                            );
                                                        }
                                                        */
                                                        return getTotalRecordsCount()
                                                            .then(result => {
                                                                const count = result.count;
                                                                api?.setRowCount(count); // ex setInfiniteRowCount
                                                                successCallback(rows, count);
                                                                updateInfiniteLastRowMax(count);
                                                                return 3;
                                                            })
                                                            .catch(exception => {
                                                                if (APP_INFO.debug) {
                                                                    console.debug(newFetchPageParams, { exception });
                                                                }
                                                                notifyFetchError(exception);
                                                                return 4;
                                                            })
                                                            .finally(() => {
                                                                done(true); // 𝘸𝘪𝘵𝘩 𝘤𝘰𝘶𝘯𝘵
                                                            });
                                                    }
                                                }
                                            }
                                        } else {
                                            /**
                                             * When requestStillValid() returns false, handle this as 
                                             * failed case in catch by returning a rejected promise.
                                             * Data will be fetched again in the next request.
                                             */
                                            return Promise.reject();
                                        }
                                    })
                                    .catch(exception => {
                                        if (APP_INFO.debug) {
                                            console.debug(newFetchPageParams, { exception });
                                        }
                                        if (exception) {
                                            notifyFetchError(exception);
                                        }
                                        handleNoRow(false);
                                        setNoRow(true);
                                        // hides loader and Leaves the grid in a failed state
                                        failCallback();
                                        return 4;
                                    })
                                    .then(caseN => {
                                        done(true);
                                        if (newFetchPageParams === ref.lastFetchPageParams) {
                                            ref.lastFetchPageParams = null;
                                        }
                                    });
                                } else {
                                    done(true);
                                    refreshTable();
                                }
                            }
                        }, 150);
                    }
                    /**-/
                    else {
                        console.log('%cduplicate%O', 'background:yellow', newFetchPageParams);
                    }
                    /**/
                }
              }
            : null;

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const enrichedColumnDefs = [];
    const customComponents = {};

    // ◰ 𝘀𝘅
    if (checkboxSelectorLeft) {
        // noinspection JSSuspiciousNameCombination
        const checkboxSelectorLeftWidth = rowHeight;
        const checkboxSelectorLeftColDef = noDataColFactory('checkboxSelectorLeft', {
            maxWidth: checkboxSelectorLeftWidth,
            minWidth: checkboxSelectorLeftWidth,
            width: checkboxSelectorLeftWidth,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            lockPinned: true,
            pinned: 'left'
        });
        if (isServerSide) {
            const checkboxSelectorLeftRenderId = checkboxSelectorLeftColDef.headerComponent = 'checkAllRowsFetchedRender';
            customComponents[checkboxSelectorLeftRenderId] = ({ api }) => {
                const [isChecked, setIsChecked] = useState(false);
                // todo: keep already selected when unselect some from all
                const setCheckedAllRowsAs = bool => {
                    resetRowSelections();
                    if (bool) {
                        api.getRenderedNodes().forEach(e => {
                            e.setSelected(true);
                        });
                    } else {
                        api.forEachNode(e => {
                            if (e.selected) {
                                e.setSelected(false);
                            }
                        });
                    }
                    ref.shouldSelectAllRows = bool;
                    setIsChecked(bool);
                };
                getSetCheckedAllRowsAs?.(setCheckedAllRowsAs);
                ref.checkedAllRowsAsSetter = setCheckedAllRowsAs;
                return (
                    <div className={
                        `ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper ${
                            isChecked ? 'ag' : 'not'
                        }-checked`
                    }>
                        <input
                            className="ag-input-field-input ag-checkbox-input"
                            type="checkbox"
                            tabIndex="-1"
                            checked={isChecked}
                            onChange={syntheticEvent => {
                                setCheckedAllRowsAs(
                                    syntheticEvent.currentTarget.checked
                                );
                            }}
                        />
                    </div>
                );
            };
        }
        enrichedColumnDefs.push(checkboxSelectorLeftColDef);
    }

    // ⎅ 𝗺𝗱
    if (columnDefs?.length) {
        let lastCenterDef = {};
        for (const columnDef of columnDefs) {
            if (columnDef.hasOwnProperty('type')) {
                const colType = columnDef.type;
                const defaultColType = 'TEXT';
                /* 𝘦.𝘨.:
                    customFieldsNumbers.TEXT -> 0
                    customFieldsLiteral[0] -> 'TEXT'
                */
                // noinspection UnnecessaryLocalVariableJS
                const validatedColType =
                    isNaN(colType)
                        ? customFieldsNumbers.hasOwnProperty(colType)
                            ? colType
                            : defaultColType
                        : customFieldsLiterals[colType] || defaultColType;

                // console.log({ colType, validatedColType, columnDef });
                columnDef.type = validatedColType;
            }
            columnDef.headerName = translate(
                columnDef.headerName || toHeaderName(columnDef.field)
            );
            if (!columnDef.hasOwnProperty('pinned')) {
                columnDef.pinned = null;// 𝘶𝘯𝘱𝘪𝘯 𝘱𝘳𝘦𝘷𝘪𝘰𝘶𝘴𝘭𝘺 𝘱𝘪𝘯𝘯𝘦𝘥
            }
            lastCenterDef = columnDef; // 𝘶𝘱𝘥𝘢𝘵𝘦 𝘸𝘪𝘵𝘩 𝘢 𝘷𝘢𝘭𝘪𝘥 𝘤𝘰𝘭𝘶𝘮𝘯…
            enrichedColumnDefs.push(columnDef);
        }
        // ag-grid.com/react-data-grid/column-sizing/#column-flex
        lastCenterDef.flex = 1; // 𝘨𝘳𝘰𝘸 𝘰𝘧 𝘭𝘢𝘴𝘵 𝘤𝘰𝘭𝘶𝘮𝘯
        if (enrichedColumnDefs.length > 1) {
            lastCenterDef.suppressSizeToFit = false;
            enrichedColumnDefs[0].flex = null;
        }
    }

    // ◳ 𝗱𝘅
    if (iconsInRow?.length) {
        const iconsInRowVisibleOnHover = [];
        const iconsInRowVisibleAlways = [];
        for (const e of iconsInRow) {
            (e.fixed
                ? iconsInRowVisibleAlways
                : iconsInRowVisibleOnHover
            ).push(e);
        }
        const visibleIcons = iconsInRowVisibleAlways.length;
        const iconsInRowWidth = visibleIcons ? 24 * (visibleIcons + 1) : 1; // still used!
        const iconsInRowColDef =  noDataColFactory(iconsInRowColId, {
            width: iconsInRowWidth,
            maxWidth: iconsInRowWidth,
            minWidth: iconsInRowWidth,
            cellClass: 'cell-iconsInRow',
            lockPinned: true,
            pinned: 'right'
        });
        const iconsInRowRenderId = iconsInRowColDef.cellRenderer = 'iconsInRowRender';
        customComponents[iconsInRowRenderId] = params => {
            if (params.data) {
                const groupVisibleOnHover = createIconsInRowGroup(
                    iconsInRowVisibleOnHover,
                    'visible-onhover',
                    params,
                    forceTooltip
                );
                const groupVisibleAlways = createIconsInRowGroup(
                    iconsInRowVisibleAlways,
                    'visible-always',
                    params,
                    forceTooltip
                );
                if (
                    groupVisibleOnHover ||
                    groupVisibleAlways
                ) {
                    return (
                        <div className="iconsInRow-wrapper">
                            <span className="icons">
                                {groupVisibleOnHover}
                                {groupVisibleAlways}
                            </span>
                        </div>
                    );
                }
            }
            return ''; // instead of null, see: https://github.com/ag-grid/ag-grid/issues/3222
        };
        enrichedColumnDefs.push(iconsInRowColDef);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    useEffect(() => {
        ref.isMounted = true;
        if (props.hasOwnProperty('rows')) {
            // isClientSide 𝘢𝘯𝘥 𝘳𝘰𝘸𝘴 𝘢𝘳𝘦 𝘥𝘪𝘳𝘦𝘤𝘵𝘭𝘺 𝘱𝘢𝘴𝘴𝘦𝘥
        } else if (isServerSide) {
            // 𝘢𝘶𝘵𝘰𝘮𝘢𝘵𝘪𝘤𝘢𝘭𝘭𝘺 𝘶𝘴𝘦 𝘱𝘳𝘰𝘷𝘪𝘥𝘦𝘥 datasource.getRows
        } else if (fetchDataOnce) {
            setFetchingData(true);
            fetchDataOnce()
                .then(response => {
                    if (200 !== response.status) {
                        throw response;
                    } else {
                        return response.data;
                    }
                })
                .catch(exception => {
                    notifyFetchError(exception);
                    return null;
                })
                .then(data => {
                    // 𝘧𝘪𝘯𝘢𝘭𝘭𝘺
                    if (ref.isMounted) {
                        setFetchingData(false);
                        setRowsDataOnce(data);
                    }
                });
        }
        return () => {
            ref.isMounted = false;
            ref.canSetDatasource = true;
            ref.lastFetchPageParams = null;
            ref.lastRowClicked = null;
            ref.unselectedRows = null;
            ref.selectedRows  = null;
            ref.gridApi = null;
            ref.tableMap.clear();
            for (const e of [
                ref.getRowsTimeout,
                ref.targetRowTimeout,
                ref.refreshTableTimeout,
                ref.paginationLockedTimeout,
                ref.firstDataRenderedTimeout
            ]){
                clearTimeout(e);
            }
        };
    }, []);

    /*
    useEffect(() => {
        if (rowsAreInfinite) {
            const n = rowsPerPage * 3;
            setCacheBlockSize(n);
            setMaxBlocksInCache(n * 100);
        }
    }, [rowsPerPage]);
    */

    useEffect(() => {
        clearTimeout(ref.targetRowTimeout);
        if (targetRow) {
            const api = ref.gridApi?.api;
            if (api) {
                if (pagination) {
                    ref.targetRowTimeout = setTimeout(() => {
                        // 𝘧𝘪𝘯𝘥 𝘱𝘢𝘨𝘦 𝘵𝘩𝘢𝘵 𝘤𝘰𝘯𝘵𝘢𝘪𝘯𝘴 𝘳𝘰𝘸 𝘢𝘯𝘥 𝘨𝘰𝘵𝘰... 0 𝘪𝘴 𝘧𝘪𝘳𝘴𝘵 𝘱𝘢𝘨𝘦.
                        const index = targetRow.rowIndex;
                        const pageNum = index && Math.floor(index / api.paginationGetPageSize());
                        api.paginationGoToPage(pageNum);
                        api.ensureIndexVisible(index);
                    }, 300);
                }
            }
        }
    }, [targetRow]);

    useEffect(() => {
        if (isServerSide) {
            if (filterString !== ref.prevFilterString) {
                ref.prevFilterString = filterString;
                if (ref.canSetDatasource) {
                    // 𝖺𝗌𝗌𝗂𝗀𝗇 𝗍𝗁𝖾 𝖽𝖺𝗍𝖺𝗌𝗈𝗎𝗋𝖼𝖾 𝗍𝗈 𝗂𝗍𝗌𝖾𝗅𝖿 𝖺𝗀𝖺𝗂𝗇 𝗐𝗂𝗅𝗅 𝗍𝗋𝗂𝗀𝗀𝖾𝗋 𝖺 𝗋𝖾𝖿𝗋𝖾𝗌𝗁 𝗈𝖿 𝖽𝖺𝗍𝖺
                    ref.gridApi?.api.setDatasource(datasource);
                    ref.canSetDatasource = false; // <<
                }
            }
        }
        resetRowSelections();
    }, [filterString]);

    useEffect(() => {
        const api = ref.gridApi?.api;
        if (api) {
            const hasDetail = !!currentDetail;
            const columnApi = api.gridOptionsWrapper?.getColumnApi();
            if (ref.collapseColumns) {
                if (!hasDetail) {
                    const colIdsToShowAgain = ref.collapseColumns;
                    if (colIdsToShowAgain) {
                       ref.collapseColumns = false;
                       columnApi?.setColumnsVisible(colIdsToShowAgain, true);
                    }
                }
            } else {
                if (hasDetail) {
                    const colIdsToHide = [];
                    const columnDefs = api.getColumnDefs();
                    if (columnDefs?.length) {
                        for (const colDef of columnDefs) {
                            if (
                                colDef.hide !== undefined &&
                                !colDef.hide &&
                                noDataColType !== colDef.type
                            ) {
                                colIdsToHide.push(colDef.colId);
                            }
                        }
                    }
                    ref.collapseColumns = colIdsToHide;
                    if (colIdsToHide.length) {
                        colIdsToHide.shift();
                        columnApi?.setColumnsVisible(colIdsToHide, false);
                    }
                }
            }
        }
    }, [currentDetail]);

    useEffect(() => {
        // console.log({ fetchingData });
        if (rowsAreInfinite) {
            if (enableGoToPage) {
                const inputGoToPage = ref.tableMap.get(inputGoToPageKey);
                if (inputGoToPage) {
                    inputGoToPage.disabled = fetchingData;
                }
            }
        }
    }, [fetchingData]);

    useEffect(() => {
        const shouldSelectAllRowsCurrent = ref.shouldSelectAllRows;
        if (showSelectedRowsCounter && ref.gridApi?.api) {
            // 𝘢𝘴𝘴𝘶𝘮𝘦 𝘵𝘢𝘣𝘭𝘦 𝘥𝘰𝘮 𝘦𝘹𝘪𝘴𝘵
            setlbSelectedRowsCounter(
                shouldSelectAllRowsCurrent
                    ? translate('All rows selected')
                    : ''
            );
        }
        handleSelectAllRows?.(shouldSelectAllRowsCurrent);
    }, [ref.shouldSelectAllRows]);

    useEffect(() => {
        getUnselectedRows?.(ref.unselectedRows);
    }, [ref.unselectedRows]);

    /**-/
    useEffect(() => {
        console.debug({
            selectedRows: ref.selectedRows,
            unselectedRows: ref.unselectedRows
        });
    }, [
        ref.selectedRows,
        ref.selectedRows?.length
    ]);
    /**/

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const handleRowClicked = params => {
        if (
            !disabled &&
            !window.getSelection().toString().length
        ) {
            const { node, api } = params;
            const nodeData = node?.data; // cfr params.data undefined
            const isDeletedRow =  nodeData && (nodeData.is_deleted || nodeData.deleted);
            const id = nodeData?.[rowKey]; // 'id' is for row node
            if (
                isDeletedRow &&
                ignoreDeletedRowClick
            ) {
                // console.info('ignored click @', { id, programmatic });
            } else {
                const prev = ref.lastRowClicked;
                const prevId = prev?.data?.[rowKey];
                const next = prev ? id !== prevId : true;
                // noinspection RedundantConditionalExpressionJS
                const isActive = (next || !prev) ? true : false;
                const theLastRowClicked = ref.lastRowClicked = (next && id) ? node : null;
                if (highlightRowOnClick) {
                    setTimeout(() => {
                        api.redrawRows(); // see question: 60653847/ag-grid-rowclassrule-not-updating-after-using-updaterowdata
                    });
                }
                onRowClicked?.(
                    params,
                    {
                        id,
                        prev,
                        prevId,
                        rowKey,
                        next,
                        isActive,
                        theLastRowClicked
                    }
                );
            }
        }
    };

    const shouldBeClicked = row => {
        const api = ref.gridApi?.api;
        if (api) {
            const trigger = elem => {
                handleRowClicked({
                    api,
                    node: elem,
                    programmatic: true
                });
            };
            if (row && 'none' !== row) {
                // 𝗇𝗈𝗍𝖾: 𝘪𝘥𝘴 𝘥𝘦𝘱𝘦𝘯𝘥𝘴 𝘣𝘺 𝘳𝘰𝘸𝘒𝘦𝘺
                const rowId = row?.id;
                const lastRowClickedId = ref.lastRowClicked?.id;
                if (
                    lastRowClickedId
                        ? rowId && rowId !== lastRowClickedId
                        : rowId
                ) {
                    const rowNode = api.getRowNode(rowId);
                    if (rowNode) {
                        setTargetRow(rowNode);
                    }
                    trigger(rowNode);
                } else {
                    api.redrawRows()
                }
            } else {
                trigger(null);
            }
        } // else { console.info('shouldBeClicked was ignored (no api)') }
    };

    useEffect(() => {
        shouldBeClicked(rowToBeClicked);
    }, [rowToBeClicked]);

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const propsRowsLookup = () => propsRows ? propsRows.length < 1 : false;

    const [noRow, setNoRow] = useState(propsRowsLookup());

    useEffect(() => {
        const bool = propsRowsLookup();
        if (bool !== noRow) {
            setNoRow(bool);
        }
    }, [propsRows]); // 𝘤𝘭𝘪𝘦𝘯𝘵𝘚𝘪𝘥𝘦

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const paginationChangedFinally = params => {
        // console.debug({ keepRenderedRows, newPage, newData} = params);
        if (ref.isMounted) {
            setPaginationLocked(true);
            clearTimeout(ref.paginationLockedTimeout);
            ref.paginationLockedTimeout = setTimeout(() => {
                setPaginationLocked(false);
            }, 300);
            onPaginationChanged?.(params);
        }
    };

    const returnRowsData = () => {
        if(isServerSide) {
            return null;
        } else {
            if(propsRows) {
                if(propsRows.length && rowKey) {
                    if(propsRows[0][rowKey]) {
                        return propsRows;
                    } else {
                        return propsRows.map?.((row, index) => {
                            row[rowKey] = index + 1;
                            return row;
                        }) || [];
                    }
                }
            } else {
                return rowsDataOnce;
            }
        }
    };

    // Helper function to check if filterString matches preferences context values
    const checkIsFilterFromPreferences = () => {
        if (!preferencesContext || !filterString) return false;

        // bookmarkSelection is stored as a string, not an object
        // Keep fallback for .value in case of legacy object-shaped values
        const bookmarkValue = typeof preferencesContext.bookmarkSelection === 'string'
            ? preferencesContext.bookmarkSelection
            : preferencesContext.bookmarkSelection?.value;

        return (
            filterString === preferencesContext.filterBarString ||
            filterString === bookmarkValue
        );
    };

    const clearFilter = () => {
        // Use custom callback if provided
        if (onClearFilter) {
            onClearFilter();
            return;
        }

        // Only use clearFilterBar if the filterString is actually derived from PreferencesContext
        // (i.e., equals filterBarString or bookmarkSelection)
        const isPreferencesDriven = checkIsFilterFromPreferences();

        if (isPreferencesDriven && preferencesContext.clearFilterBar) {
            preferencesContext.clearFilterBar({
                savePreferences: true
            });
        }
        // If not preferences-driven and no onClearFilter provided, do nothing
        // (the button should be hidden in this case)
    };

    // Determine if the Reset button should be shown
    const shouldShowClearFilter = () => {
        // Always show if custom onClearFilter is provided
        if (onClearFilter) {
            return true;
        }

        // Show if filterString is derived from PreferencesContext
        const isPreferencesDriven = checkIsFilterFromPreferences();

        return isPreferencesDriven;
    };

    // console.count('render Table2');
    return (
        <span
            id={id}
            data-testid={testid || null}
            ref={getTableWrapperRef}
            className={
                `${prefix_table2}-wrapper ${
                    props.className || 'standard'
                } last-row-max-${
                    lastRowMax
                }`
            }
        >
            <div
                className={
                    `${prefix_table2} ${
                        isServerSide ? `${validatedRowModelType}-row server` : 'client'
                    }-side ${
                        fetchingData ? 'is' : 'not'
                    }-fetching-data ${
                        fetchingDbCount ? 'is' : 'not'
                    }-fetching-dbcount ${
                        noRow ? 'no' : 'has'
                    }-row ${
                        iconsInRow?.groups?.iconsInRowElementsVisibleAlways?.length ? 'has' : 'no'
                    }-iconsinrow-always-visible ${
                        firstDataRendered ? 'is' : 'not'
                    }-${suffix_firstDataRendered} ${
                        paginationLocked ? 'is' : 'not'
                    }-pagination-locked`
                }
            >
                {(showHeader && header)
                    ? <div className={`${prefix_table2}-header`}>
                        {header}
                      </div>
                    : null
                }
                <div className={`${prefix_table2}-body`}>
                    {((
                        datasource ||
                        rowsDataOnce ||
                        propsRows
                    ) && (
                        props.hasOwnProperty('iconsInRow')
                            ? iconsInRow  // 𝘴𝘩𝘰𝘶𝘭𝘥 𝘦𝘹𝘪𝘴𝘵, 𝘦𝘮𝘱𝘵𝘺 𝘰𝘳 𝘯𝘰𝘵.
                            : true
                    ))
                        ? <>
                            <Suspense fallback={<Loader />}>
                            <span className={`grid-wrapper w-${noRow ? '100' : 'as-defined'}`}>
                                { // Grid 𝘴𝘵𝘪𝘭𝘭 𝘦𝘹𝘪𝘴𝘵
                                    <NoRowsPlaceholder
                                        visible={noRow && !fetchingData}
                                        searched={0 < filterString?.length}
                                        delay={600}
                                        clearFilter={shouldShowClearFilter() ? clearFilter : null}
                                        createButtonShown={createButtonShown}
                                    />
                                }
                                <Grid
                                    {
                                        ...{
                                            ...rest,
                                            ...proxied,
                                            id:
                                                id?.length
                                                    ? id + '-grid'
                                                    : null,

                                            columnTypes:
                                                columnTypes,

                                            defaultColDef:
                                                Object.assign(
                                                    {
                                                        type: 'TEXT',
                                                        suppressMovable: true,
                                                        resizable: true,
                                                        sortable: !isServerSide,
                                                        minWidth: 180,
                                                        flex: 1
                                                    },
                                                    defaultColDef
                                                ),

                                            columnDefs:
                                                enrichedColumnDefs,

                                            onColumnResized:
                                                onColumnResized
                                                    ?? (params => {
                                                        clearTimeout(ref.columnResizeTimeout);
                                                        ref.columnResizeTimeout = setTimeout(() => {
                                                            if (
                                                                params.finished &&
                                                                'uiColumnDragged' ===  params.source // 𝘥𝘳𝘢𝘨𝘨𝘦𝘥 𝘮𝘢𝘯𝘶𝘢𝘭𝘭𝘺
                                                            ) {
                                                                const { columnApi } = params;
                                                                const col = columnApi.getDisplayedCenterColumns()?.pop();
                                                                if (col) {
                                                                    col.flex = 1;
                                                                    columnApi.applyColumnState({
                                                                        state: columnApi.getColumnState()
                                                                    });
                                                                }
                                                            }
                                                        }, 350);
                                                    }),

                                            onCellFocused:
                                                onCellFocused
                                                    ? params => {
                                                        if (shouldConsiderRowEventCallback(params)) {
                                                            onCellFocused(params);
                                                        }
                                                      }
                                                    : null,

                                            onCellMouseOver:
                                                iconsInRow && !suppressRowClickSelection
                                                    ? params => {
                                                        if (iconsInRowColId === params.colDef.colId) {
                                                            const el = params.event?.target; // 𝘪𝘤𝘰𝘯-𝘣𝘶𝘵𝘵𝘰𝘯
                                                            if (el) {
                                                                el.removeEventListener('click', el.preventRowSelectionByClickFn);
                                                                const fn = el.preventRowSelectionByClickFn = () => {
                                                                    clearTimeout(fn.timeout);
                                                                    const gridOptions = params.api.gridOptionsWrapper.gridOptions;
                                                                    gridOptions.suppressRowClickSelection = true;
                                                                    fn.timeout = setTimeout(()=>{
                                                                        el.suppressIconsInRowClickSelectionFn = null;
                                                                        gridOptions.suppressRowClickSelection = false;
                                                                    }, 320);
                                                                };
                                                                el.addEventListener('click', fn)
                                                            }
                                                        }
                                                      }
                                                    : null,

                                            infiniteInitialRowCount: rowsPerPage,
                                            paginationPageSize: rowsPerPage,
                                            cacheBlockSize: rowsPerPage,

                                            rowModelType:
                                                validatedRowModelType,

                                            rowData:
                                                returnRowsData(),

                                            /*
                                            AG Grid: since v27.1, `getRowNodeId` is deprecate and has been replaced by `getRowId`.
                                            The difference is if getRowId() is implemented, immutable data is on by default.'
                                            https://www.ag-grid.com/javascript-data-grid/
                                            - grid-callbacks/#reference-callbacks-getRowId
                                            - accessing-data/#accessing-rows-by-row-id
                                            */

                                            getRowId:
                                                params => params.data[rowKey],

                                            onRowClicked:
                                                onRowClicked
                                                    ? 'function' !== typeof props.onRowDoubleClicked
                                                        ? params => {
                                                            if (shouldConsiderRowEventCallback(params)) {
                                                                handleRowClicked(params);
                                                            }
                                                          }
                                                        : params => {
                                                            if (shouldConsiderRowEventCallback(params)) {
                                                                ref.rowClicksCount ||= 0;
                                                                const clicks = ++ref.rowClicksCount;
                                                                if (2 > clicks) {
                                                                    if (1 === clicks) {
                                                                        clearTimeout(ref.rowClickTimeout);
                                                                        ref.rowClickTimeout = setTimeout(() => {
                                                                            ref.rowClicksCount = 0;
                                                                            handleRowClicked(params);
                                                                        }, 320);
                                                                    }
                                                                } else {
                                                                    clearTimeout(ref.rowClickTimeout);
                                                                    ref.rowClicksCount = 0;
                                                                }
                                                            }
                                                          }
                                                    : null,

                                            onRowSelected:
                                                params => {
                                                    const {
                                                        node: {
                                                            selected: rowIsSelected
                                                        },
                                                        data
                                                    } = params;
                                                    const removeFrom = list => {
                                                        if (list?.length) {
                                                            const index = list.indexOf(data);
                                                            if (-1 < index) {
                                                                list.splice(index, 1);
                                                            }
                                                        }
                                                    };
                                                    const addTo = list => {
                                                        if (list) {
                                                            if (list.length) {
                                                                if (0 > list.indexOf(data)) {
                                                                    list.push(data);
                                                                }
                                                            } else {
                                                                list[0] = data;
                                                            }
                                                        }
                                                    };
                                                    const shouldSelectAllRowsCurrent = ref.shouldSelectAllRows;
                                                    const selectedRowsCurrent = ref.selectedRows || [];
                                                    const unselectedRowsCurrent = ref.unselectedRows || [];
                                                    if (shouldSelectAllRowsCurrent) {
                                                        (rowIsSelected ? removeFrom : addTo)(unselectedRowsCurrent);
                                                    } else {
                                                        (rowIsSelected ? addTo : removeFrom)(selectedRowsCurrent);
                                                    }
                                                    const selectionInfo = {
                                                        selectedAll: shouldSelectAllRowsCurrent,
                                                        unselectedRows: unselectedRowsCurrent,
                                                        selectedRows: selectedRowsCurrent
                                                    };
                                                    /**-/
                                                    console.groupCollapsed('onRowSelected:', selectionInfo);
                                                    console.trace(params);
                                                    console.groupEnd();
                                                    /**/
                                                    onRowSelected?.(selectionInfo);
                                                },

                                            onSelectionChanged:
                                                params => {
                                                    const shouldSelectAllRowsCurrent = ref.shouldSelectAllRows;
                                                    let selectedAllFlag = shouldSelectAllRowsCurrent;
                                                    const selectedRowsCurrent = ref.selectedRows || [];
                                                    const unselectedRowsCurrent = ref.unselectedRows || [];
                                                    if (showSelectedRowsCounter) {
                                                        const n = (
                                                            shouldSelectAllRowsCurrent
                                                                ? unselectedRowsCurrent
                                                                : selectedRowsCurrent
                                                        )?.length;
                                                        const msgCount = int => {
                                                            // console.debug('msgCount', {int, shouldSelectAllRowsCurrent});
                                                            if (
                                                                shouldSelectAllRowsCurrent &&
                                                                0 === int
                                                            ) {
                                                                selectedAllFlag = false;
                                                                ref.checkedAllRowsAsSetter?.(false);
                                                            }
                                                            return translate(
                                                                '{count} rows checked',
                                                                {
                                                                    count: int
                                                                }
                                                            );
                                                        };
                                                        setlbSelectedRowsCounter(
                                                            shouldSelectAllRowsCurrent
                                                                ? n
                                                                    ? msgCount(
                                                                        params.api[
                                                                            `${
                                                                                rowsAreInfinite
                                                                                    ? 'getInfinite'
                                                                                    : 'paginationGet'
                                                                            }RowCount`
                                                                        ]() - n
                                                                      )
                                                                    : translate('All rows selected')
                                                                : n
                                                                    ? msgCount(n)
                                                                    : ''
                                                        );
                                                    }
                                                    const selectionInfo = {
                                                        selectedAll: selectedAllFlag,
                                                        unselectedRows: unselectedRowsCurrent,
                                                        selectedRows: selectedRowsCurrent
                                                    };
                                                    /**-/
                                                    console.groupCollapsed('onSelectionChanged:', selectionInfo);
                                                    console.trace(params);
                                                    console.groupEnd();
                                                    /**/
                                                    onSelectionChanged?.(selectionInfo);
                                                },

                                            onPaginationChanged:
                                                pagination
                                                    ? rowsAreInfinite
                                                        ? params => {
                                                            const { api } = params;
                                                            const visibleRowsCount = api.paginationGetPageSize();
                                                            const lastRowIndex = api.getInfiniteRowCount();
                                                            const recordCountInfinite = ref.tableMap.get(lbRecordCountInfiniteKey);
                                                            if (recordCountInfinite) {
                                                                if (lastRowIndex !== parseInt(recordCountInfinite.textContent)) {
                                                                    updateInfiniteLastRowMax(lastRowIndex);
                                                                }
                                                            }
                                                            if (enableGoToPage) {
                                                                const inputGoToPage = ref.tableMap.get(inputGoToPageKey);
                                                                if (inputGoToPage) {
                                                                    inputGoToPage.style.display = 'none';
                                                                    const lastPageNum = lastRowIndex ? Math.ceil(lastRowIndex / visibleRowsCount) : 1;
                                                                    inputGoToPage.setAttribute('max', lastPageNum);
                                                                    inputGoToPage.maxAsNumber = lastPageNum;
                                                                }
                                                                const lbCurrent = ref.tableMap.get(lbCurrentKey);
                                                                if (lbCurrent) {
                                                                    lbCurrent.style.display = 'inline';
                                                                }
                                                            }
                                                            if (ref.shouldSelectAllRows) {
                                                                // 𝘴𝘦𝘭𝘦𝘤𝘵 𝘤𝘩𝘦𝘤𝘬𝘣𝘰𝘹𝘦𝘴 𝘰𝘧 𝘯𝘦𝘸 𝘱𝘢𝘨𝘦
                                                                const unselectedRowsCurrent = ref.unselectedRows || [];
                                                                const unselectedRowsCurrentLength = unselectedRowsCurrent.length;
                                                                api.getRenderedNodes().forEach(
                                                                    unselectedRowsCurrentLength
                                                                        ? e => {
                                                                            e.setSelected(!unselectedRowsCurrent.includes(e.data));
                                                                          }
                                                                        : e => {
                                                                            e.setSelected(true);
                                                                          }
                                                                );

                                                            }
                                                            paginationChangedFinally(params);
                                                          }
                                                        : params => {
                                                            const n = propsRows?.length ?? 0; // 𝘧𝘪𝘯𝘪𝘵𝘦 𝘳𝘰𝘸𝘴
                                                            if (lastRowMax !== n) {
                                                                setLastRowMax(n);
                                                            }
                                                            paginationChangedFinally(params);
                                                          }
                                                    : null,

                                            onFirstDataRendered:
                                                params => {
                                                    clearTimeout(ref.firstDataRenderedTimeout);
                                                    if (onFirstDataRendered?.(params)) {
                                                        setFirstDataRendered(true);
                                                    } else {
                                                        ref.firstDataRenderedTimeout = setTimeout(() => {
                                                            params.api?.sizeColumnsToFit?.();
                                                            setFirstDataRendered(true);
                                                        }, 250);
                                                    }
                                                    // Q: should be moved inside timeout?
                                                    if (externalRowSelection?.length) {
                                                        params.api?.forEachNode(e => {
                                                            if (externalRowSelection.includes(e.id)) {
                                                                e.setSelected(true);
                                                            }
                                                        });
                                                    }
                                                },

                                            onGridReady:
                                                params => {
                                                    const { api } = ref.gridApi = params;
                                                    if (datasource) {
                                                        api.gridOptionsWrapper.gridOptions.datasource = datasource;
                                                        // from v26.0.0 also we need:
                                                        api.setDatasource(datasource);
                                                    }
                                                    const {
                                                        gridOptionsWrapper: {
                                                            gridOptions,
                                                            environment: {
                                                                eGridDiv // instead of 'eGui'
                                                            }
                                                        },
                                                        paginationProxy
                                                    } = api;
                                                    // console.debug({gridOptions});
                                                    if (eGridDiv) {
                                                        const agPagingPanel = eGridDiv.querySelector('.ag-paging-panel');
                                                        if (agPagingPanel) {
                                                            if (rowsAreInfinite) {
                                                                const addNodeAfterPagingElement = (tag, attrs, key, selector) => {
                                                                    const sibling = agPagingPanel.querySelector(selector);
                                                                    if (sibling) {
                                                                        const node = sibling.ownerDocument.createElement(tag);
                                                                        if (attrs) {
                                                                            for (const [attrName, attrValue] of Object.entries(attrs)) {
                                                                                node.setAttribute(attrName, attrValue);
                                                                            }
                                                                        }
                                                                        sibling.after(node);
                                                                        ref.tableMap.set(key,node);
                                                                        return [node,sibling];
                                                                    } else {
                                                                        return [];
                                                                    }
                                                                };
                                                                addNodeAfterPagingElement(
                                                                    'b',
                                                                    {
                                                                        class:'last-row'
                                                                    },
                                                                    lbRecordCountInfiniteKey,
                                                                    '.ag-paging-row-summary-panel [ref="lbRecordCount"]'
                                                                );
                                                                if (enableGoToPage) {
                                                                    const [inputGoToPage, lbCurrent] = addNodeAfterPagingElement(
                                                                        'input',
                                                                        {
                                                                            class: 'goto-pageNum',
                                                                            type: 'text', // 'number' 𝘩𝘢𝘴 𝘶𝘯𝘸𝘢𝘯𝘵𝘦𝘥 𝘢𝘳𝘳𝘰𝘸 𝘴𝘱𝘪𝘯𝘯𝘦𝘳
                                                                            step: 1
                                                                        },
                                                                        inputGoToPageKey,
                                                                        '.ag-paging-page-summary-panel .ag-paging-number[ref="lbCurrent"]'
                                                                    );
                                                                    ref.tableMap.set(lbCurrentKey, lbCurrent);
                                                                    if (lbCurrent) {
                                                                        lbCurrent.classList.add('toggle-gotopage');
                                                                        lbCurrent.onclick = null;
                                                                        lbCurrent.addEventListener('click', () => {
                                                                            if (!fetchingData && !fetchingDbCount) {
                                                                                lbCurrent.style.display = 'none';
                                                                                const e = inputGoToPage.style;
                                                                                e.width = e.minWidth = `${Math.max(3, String(paginationProxy.totalPages).length)}em`;
                                                                                e.display = 'inline-block';
                                                                                inputGoToPage.focus();
                                                                            }
                                                                        });
                                                                        Object.defineProperty(inputGoToPage, 'valueAsNumber', {
                                                                            get() {
                                                                                const n = this.value;
                                                                                return n ? parseInt(n,10) : 0;
                                                                            },
                                                                            set(n) {
                                                                                this.value = n;
                                                                            }
                                                                        });
                                                                        inputGoToPage.disabled = fetchingData;
                                                                        inputGoToPage.minAsNumber = 1;
                                                                        inputGoToPage.maxAsNumber = 1;
                                                                        const inputGoToPageOff = () => {
                                                                            lbCurrent.style.display = 'inline';
                                                                            inputGoToPage.style.display = 'none';
                                                                            inputGoToPage.valueAsNumber = 0;
                                                                            inputGoToPage.value = '';
                                                                        };
                                                                        inputGoToPageOff();
                                                                        inputGoToPage.addEventListener('blur',inputGoToPageOff);
                                                                        inputGoToPage.addEventListener('keypress', event => {
                                                                            const keyUnicode = event.which || event.keyCode;
                                                                            if (13 !== keyUnicode) {
                                                                                if (keyUnicode < 48 || keyUnicode > 57) {
                                                                                    event.stopImmediatePropagation();
                                                                                    event.preventDefault();
                                                                                    return false;
                                                                                } else {
                                                                                    return true;
                                                                                }
                                                                            } else {
                                                                                // 𝘱𝘳𝘦𝘴𝘴𝘦𝘥 '𝖤𝗇𝗍𝖾𝗋'
                                                                                if (inputGoToPage.value?.length) {
                                                                                    const valNum = inputGoToPage.valueAsNumber;
                                                                                    gridOptions.api.paginationGoToPage(
                                                                                        valNum > 0
                                                                                            ? valNum - 1
                                                                                            : 0 // 𝘣𝘦𝘤𝘢𝘶𝘴𝘦 𝘱𝘢𝘨𝘦 𝘪𝘯𝘥𝘦𝘹 𝘪𝘴 𝘻𝘦𝘳𝘰 𝘣𝘢𝘴𝘦𝘥
                                                                                    );
                                                                                    inputGoToPageOff();
                                                                                }
                                                                            }
                                                                        });
                                                                        inputGoToPage.addEventListener('input', () => {
                                                                            const maxNum = inputGoToPage.maxAsNumber;
                                                                            const minNum = inputGoToPage.minAsNumber;
                                                                            const valNum = inputGoToPage.valueAsNumber;
                                                                            if (valNum > maxNum) {
                                                                                inputGoToPage.value = inputGoToPage.valueAsNumber = maxNum;
                                                                            } else if (valNum < minNum) {
                                                                                inputGoToPage.value = inputGoToPage.valueAsNumber = minNum;
                                                                            } else {
                                                                                inputGoToPage.valueAsNumber = valNum;
                                                                            }
                                                                        });
                                                                    }
                                                                } // else finite rows…
                                                            }
                                                            if (showSelectedRowsCounter) {
                                                                // 𝘣𝘰𝘵𝘩 𝘪𝘯𝘧𝘪𝘯𝘪𝘵𝘦 𝘢𝘯𝘥 𝘧𝘪𝘯𝘪𝘵𝘦 𝘳𝘰𝘸𝘴
                                                                const counterCssClass = 'selected-rows-counter';
                                                                let node = agPagingPanel.querySelector(`.${counterCssClass}`);
                                                                if (!node) {
                                                                    node = agPagingPanel.ownerDocument.createElement('span');
                                                                    node.className = `${counterCssClass} text-primary`;
                                                                    agPagingPanel.insertBefore(node,agPagingPanel.firstChild);
                                                                }
                                                                ref.tableMap.set(lbSelectedRowsCounterKey, node);
                                                            }
                                                        }
                                                    }
                                                    onGridReady?.(params);
                                                },

                                            // ag-grid.com/javascript-data-grid/row-styles/#row-class-rules
                                            rowClassRules:
                                                Object.assign(
                                                    {
                                                        'active-row':
                                                              highlightRowOnClick
                                                                  ? params => {
                                                                      // 𝘯𝘣: 𝘵𝘩𝘦 𝘪𝘥 𝘷𝘢𝘭𝘶𝘦 𝘥𝘦𝘱𝘦𝘯𝘥 𝘣𝘺 𝘳𝘰𝘸𝘒𝘦𝘺
                                                                      const a = ref.lastRowClicked?.id;
                                                                      const b = params.data?.[rowKey];
                                                                      // noinspection EqualityComparisonWithCoercionJS
                                                                      return a && b ? a == b : false;
                                                                    }
                                                                  : null
                                                    },
                                                    defaultRowClassRules,
                                                    propsRowClassRules
                                                ),

                                            // ag-grid.com/javascript-grid-components
                                            components: // (ex frameworkComponents)
                                                Object.assign(
                                                    {
                                                        involvedIncidents: involvedIncidentsCellRender,
                                                        iconStatus: iconStatusCellRender,
                                                        html2text: html2textCellRender,
                                                        checkbox: checkboxCellRender,
                                                        color: colorCellRender,
                                                        tags: tagsCellRender,
                                                        plainText: textCellRender,
                                                        triggerLink: triggerLinkCellRender,
                                                        duration: durationCellRender
                                                        /*
                                                        todo: added component 'text' (17/03/2022) it may need to be renamed:
                                                        Since v15.0 component names have been renamed to be namespaced.
                                                        You should rename "filter:text" to "filter:blablaFilter"
                                                        see 'agDeprecatedNames' of ag-grid-community.cjs.js:17594
                                                        */
                                                    },
                                                    customComponents,
                                                    cellRenders
                                                ),

                                            // applyColumnDefOrder: true, -> update grid everytime the column order change (since v26.0 no longer needed)

                                            // debug: true
                                        }
                                    }
                                />
                                </span>
                            {
                                noRow
                                    ? null
                                    : currentDetail
                            }
                            </Suspense>
                          </>
                        : <Loader />
                    }
                </div>
            </div>
        </span>
    );
};

Table2.propTypes = {
    id: PropTypes.string,
    rowKey: PropTypes.string,
    className: PropTypes.string,
    filterString: PropTypes.string,

    pagination: PropTypes.bool,
    enableGoToPage: PropTypes.bool,
    checkboxSelectorLeft: PropTypes.bool,
    rowMultiSelectWithClick: PropTypes.bool,
    suppressRowClickSelection: PropTypes.bool,

    getSetCheckedAllRowsAs: PropTypes.func,

    getTableWrapperRef: PropTypes.func,

    getTableDataSource: PropTypes.func,
    getRefreshTable: PropTypes.func,

    getUnselectedRows: PropTypes.func,
    getRowStyle: PropTypes.func,
    getNoRow: PropTypes.func,

    onFirstDataRendered: PropTypes.func,
    onRowDoubleClicked: PropTypes.func,
    onSelectionChanged: PropTypes.func,
    onGridSizeChanged: PropTypes.func,
    onSortChanged: PropTypes.func,
    onRowClicked: PropTypes.func,
    onGridReady: PropTypes.func,
    onClearFilter: PropTypes.func,

    handleSelectAllRows: PropTypes.func,
    fetchPage: PropTypes.func,

    createButtonShown: PropTypes.bool,

    cellRenders: PropTypes.object,
    defaultColDef: PropTypes.object,
    currentDetail: PropTypes.object,

    rowToBeClicked: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

    header: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),

    iconsInRow: PropTypes.array,
    columnDefs: PropTypes.array,
    rows: PropTypes.array,

    forceTooltip: PropTypes.bool
};

export default Table2;